import {Alert, Badge, Button, Col, Container, Row} from "react-bootstrap";
import React, {useContext, useEffect, useState} from "react";
import {UsacmContext} from "../App";
import {getCourseValidation} from "../api/CourseApi";
import {getConfLabel, makePlural, titleCase} from "../utils/usacmUtils";
import {getErrorMessageForField} from "../utils/formUtils";
import {
  CONF_LABEL_KEY_COURSE,
  CONF_LABEL_KEY_COURSE_PAGE,
  COURSE_VALIDATION_ERROR,
  COURSE_VALIDATION_WARNING
} from "../constants";

/**
 * Checks a whole course and lists the problems found - see courses_utils.validate_course().
 * Errors are things a student would see broken (a {doc:key} or {video:key} with nothing behind it, a document file
 * missing from the bucket, a video not ready at MUX), warnings are probably mistakes (an unused document, no
 * instructors ...).
 * The checks run each time isActive turns on, so the results follow the edits made on the other tabs.
 */
export function CourseValidate({course, isActive, onGoToTab, onClose}) {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [problems, setProblems] = useState(null);
  const [errors, setErrors] = useState([]);
  const courseLabel = getConfLabel(conf, CONF_LABEL_KEY_COURSE).toLowerCase();
  const pageLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE_PAGE));
  const tabNames = {pages: makePlural(pageLabel), docs: 'Docs', videos: 'Videos', pricing: 'Pricing',
    instructors: 'Instructors'};
  const errorCount = (problems || []).filter(p => p.severity === COURSE_VALIDATION_ERROR).length;
  const warningCount = (problems || []).filter(p => p.severity === COURSE_VALIDATION_WARNING).length;

  function runValidation() {
    if (!course?.id) {
      return;
    }
    getCourseValidation(course.id, (code, data, newErrors) => {
      if (code === 200) {
        setProblems(data);
        setErrors([]);
      } else {
        setProblems(null);
        setErrors(newErrors);
      }
    });
  }

  useEffect(() => {
    if (isActive) {
      runValidation();
    }
  }, [course?.id, isActive]);

  // A course which has not been saved yet has nothing to check
  if (!course?.id) {
    return (
      <Container fluid className="usacm-container-wide">
        <Row>
          <Col className="text-center mt-3">
            Save this {courseLabel} before validating it.
          </Col>
        </Row>
        <Row>
          <Col className="usacm-button-row mt-3">
            <Button variant="secondary" onClick={() => onClose()}>Course List</Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="usacm-container-wide">
      <Row>
        <Col className="mb-3">
          Checks the {pageLabel.toLowerCase()} links to documents, that every document's file is in storage, and
          other likely mistakes. <b>Errors</b> are things students will see broken, <b>warnings</b> are
          probably mistakes.
        </Col>
      </Row>

      {problems &&
        <Row className="mb-3">
          <Col className="fw-bold">
            {problems.length === 0 ?
              <Alert variant="success" className="mb-0">No problems found.</Alert>
              :
              <>
                {errorCount} error{errorCount === 1 ? '' : 's'}, {warningCount} warning{warningCount === 1 ? '' : 's'}
              </>
            }
          </Col>
        </Row>
      }

      {(problems || []).map((problem, index) =>
        <Row key={index} className="mb-2">
          <Col>
            <Alert variant={problem.severity === COURSE_VALIDATION_ERROR ? 'danger' : 'warning'}
                   className="mb-0 d-flex align-items-center">
              <Badge bg={problem.severity === COURSE_VALIDATION_ERROR ? 'danger' : 'warning'}
                     text={problem.severity === COURSE_VALIDATION_ERROR ? undefined : 'dark'}
                     className="me-3">
                {problem.severity === COURSE_VALIDATION_ERROR ? 'Error' : 'Warning'}
              </Badge>
              <span className="flex-grow-1">{problem.message}</span>
              {tabNames[problem.tab] &&
                <Button size="sm" variant="outline-secondary" className="ms-3 text-nowrap"
                        onClick={() => onGoToTab(problem.tab)}>
                  Go to {tabNames[problem.tab]}
                </Button>
              }
            </Alert>
          </Col>
        </Row>
      )}

      {getErrorMessageForField(errors, '') &&
        <Row>
          <Col className="text-center mt-3 usacm-error-message">
            {getErrorMessageForField(errors, '')}
          </Col>
        </Row>
      }

      <Row>
        <Col className="usacm-button-row mt-3">
          <Button variant="secondary" onClick={() => onClose()}>Course List</Button>
          <Button onClick={() => runValidation()}>Validate Again</Button>
        </Col>
      </Row>
    </Container>
  );
}
